import axios from "axios";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Validation schema using Yup
const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),

  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),

  state: Yup.string()
    .min(2, "State must be at least 2 characters")
    .required("State is required"),

  district: Yup.string()
    .min(2, "District must be at least 2 characters")
    .required("District is required"),

  city: Yup.string()
    .min(2, "City must be at least 2 characters")
    .required("City is required"),

  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone Number is required"),

  message: Yup.string()
    .min(5, "Message must be at least 5 characters")
    .required("Message is required"),
});

const Contact = () => {
  const initialValues = {
    name: "",
    email: "",
    phone: "",
    state: "",
    district: "",
    city: "",
    message: "",
  };

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setStatus }
  ) => {
    try {
      await axios.post(
        "https://lifeonplus.in/api/contactForm",
        values
      );

      toast.success("Form submitted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setStatus({ success: "Form submitted successfully!" });
      resetForm();
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({
        error: "Failed to submit form. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">

      {/* ================= BANNER ================= */}
      <div
        className="
          contact-banner
          w-full
          max-w-[1400px]
          h-[190px]
          sm:h-[240px]
          md:h-[330px]
          lg:h-[390px]
          overflow-hidden
          rounded-b-2xl
          border-b-2
          border-[#35A93E]
          bg-white
          flex
          items-center
          justify-center
        "
      >
        <img
          src="/contactBanner.jpeg"
          alt="Contact Banner"
          className="
            w-full
            h-full
            object-contain
            object-center
            block
          "
        />
      </div>

      <div className="max-w-6xl w-full pt-10 pb-12">

        {/* ================= HEADER ================= */}
        <div className="text-center contact-heading">

          <h1
            className="
              text-sm
              sm:text-base
              md:text-lg
              font-bold
              text-[#35A93E]
              tracking-tight
              leading-tight
            "
          >
            We’re Here to Help You — Anytime, Anywhere
          </h1>

          <p
            className="
              mt-2
              text-xs
              sm:text-sm
              text-gray-600
              max-w-3xl
              mx-auto
              leading-relaxed
            "
          >
            Whether you have a question, feedback, partnership proposal, or need
            support, our team is just a message away. Reach out to us — and
            let’s build a healthier future together.
          </p>
        </div>

        {/* ================= MAIN CONTENT ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-12">

          {/* ================= CONTACT INFORMATION ================= */}
          <div
            className="
              contact-card
              lg:col-span-2
              bg-gradient-to-br
              from-white
              to-[#F5FFF2]
              rounded-2xl
              border
              border-[#E4F3E5]
              p-7
            "
          >
            <h2
              className="
                text-lg
                font-bold
                text-[#35A93E]
                mb-8
              "
            >
              Get in Touch
            </h2>

            <div className="space-y-7">

              {/* WhatsApp */}
              <div className="contact-item flex items-start space-x-4">

                <div className="contact-icon">
                  <svg
                    className="w-5 h-5 text-[#35A93E]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a2 2 0 012 1.897V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Whatsapp
                  </h3>

                  <p className="text-sm text-gray-600 mt-1">
                    99868 80000
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="contact-item flex items-start space-x-4">

                <div className="contact-icon">
                  <svg
                    className="w-5 h-5 text-[#35A93E]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Email
                  </h3>

                  <a
                    href="mailto:info@lifeonplus.com"
                    className="
                      text-sm
                      text-[#35A93E]
                      hover:text-[#2E8C36]
                      transition-colors
                    "
                  >
                    info@lifeonplus.com
                  </a>
                </div>
              </div>

              {/* Website */}
              <div className="contact-item flex items-start space-x-4">

                <div className="contact-icon">
                  <svg
                    className="w-5 h-5 text-[#35A93E]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m0 18a9 9 0 01-9-9m9 9v-18m-9 0V6m12 6H3m18 0h-6m-6 0H3"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Website
                  </h3>

                  <p className="text-sm text-gray-600 mt-1 leading-6">
                    <a
                      href="http://www.lifeonplus.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#35A93E] hover:text-[#2E8C36]"
                    >
                      www.lifeonplus.com
                    </a>

                    {" | "}

                    <a
                      href="http://www.lifeonplus.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#35A93E] hover:text-[#2E8C36]"
                    >
                      www.lifeonplus.in
                    </a>
                  </p>
                </div>
              </div>

              {/* App Support */}
              <div className="contact-item flex items-start space-x-4">

                <div className="contact-icon">
                  <svg
                    className="w-5 h-5 text-[#35A93E]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-6h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    App Support
                  </h3>

                  <p className="text-sm text-gray-600 mt-1 leading-6">
                    For app-related queries, use the in-app help section or
                    contact our support team.
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* ================= CONTACT FORM ================= */}
          <div
            className="
              contact-card
              lg:col-span-3
              bg-white
              rounded-2xl
              border
              border-[#E8F3E9]
              p-7
            "
          >
            <h2 className="text-lg font-bold text-[#35A93E] mb-5">
              Send Us a Message
            </h2>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ status, isSubmitting }) => (
                <Form className="space-y-4">

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Name
                      </label>

                      <Field
                        type="text"
                        name="name"
                        id="name"
                        placeholder="Enter your full name"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>

                      <Field
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Enter your email address"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Phone
                      </label>

                      <Field
                        type="tel"
                        name="phone"
                        id="phone"
                        placeholder="Enter your phone number"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* State */}
                    <div>
                      <label
                        htmlFor="state"
                        className="block text-sm font-medium text-gray-700"
                      >
                        State
                      </label>

                      <Field
                        type="text"
                        name="state"
                        id="state"
                        placeholder="Enter your state"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="state"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* District */}
                    <div>
                      <label
                        htmlFor="district"
                        className="block text-sm font-medium text-gray-700"
                      >
                        District
                      </label>

                      <Field
                        type="text"
                        name="district"
                        id="district"
                        placeholder="Enter your district"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="district"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* City */}
                    <div>
                      <label
                        htmlFor="city"
                        className="block text-sm font-medium text-gray-700"
                      >
                        City
                      </label>

                      <Field
                        type="text"
                        name="city"
                        id="city"
                        placeholder="Enter your city"
                        className="contact-input"
                      />

                      <ErrorMessage
                        name="city"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Interested In
                    </label>

                    <Field
                      as="textarea"
                      name="message"
                      id="message"
                      rows="3"
                      placeholder="Enter your interested product or message"
                      className="contact-input resize-none"
                    />

                    <ErrorMessage
                      name="message"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Submit */}
                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="
                        contact-submit
                        w-full
                        flex
                        justify-center
                        items-center
                        py-2.5
                        px-4
                        rounded-lg
                        text-sm
                        font-semibold
                        text-white
                        bg-[#35A93E]
                        disabled:bg-gray-400
                        disabled:cursor-not-allowed
                      "
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>

                  {/* Success */}
                  {status && status.success && (
                    <div className="text-green-600 text-sm text-center">
                      {status.success}
                    </div>
                  )}

                  {/* Error */}
                  {status && status.error && (
                    <div className="text-red-600 text-sm text-center">
                      {status.error}
                    </div>
                  )}

                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* ================= PROFESSIONAL 3D CSS ================= */}
      <style jsx>{`
        .contact-banner {
          box-shadow: 0 8px 25px rgba(53, 169, 62, 0.08);
        }

        .contact-heading {
          animation: headingReveal 0.7s ease-out both;
        }

        .contact-card {
          position: relative;
          box-shadow:
            0 8px 20px rgba(0, 0, 0, 0.045),
            0 2px 6px rgba(53, 169, 62, 0.04);
          transition:
            transform 0.35s ease,
            box-shadow 0.35s ease;
          transform: translateY(0);
        }

        .contact-card:hover {
          transform: translateY(-5px);
          box-shadow:
            0 18px 35px rgba(0, 0, 0, 0.08),
            0 5px 12px rgba(53, 169, 62, 0.07);
        }

        .contact-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border-radius: 11px;
          background: linear-gradient(
            145deg,
            #f5fff5,
            #e7f6e8
          );
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow:
            3px 3px 7px rgba(53, 169, 62, 0.10),
            -2px -2px 5px rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .contact-item {
          transition: transform 0.3s ease;
        }

        .contact-item:hover {
          transform: translateX(3px);
        }

        .contact-item:hover .contact-icon {
          transform: translateY(-2px);
          box-shadow:
            4px 5px 10px rgba(53, 169, 62, 0.13),
            -2px -2px 6px rgba(255, 255, 255, 0.95);
        }

        .contact-input {
          margin-top: 6px;
          display: block;
          width: 100%;
          border: 1px solid #ddefe0;
          border-radius: 9px;
          padding: 10px 12px;
          font-size: 14px;
          background: #ffffff;
          box-shadow:
            inset 1px 1px 3px rgba(0, 0, 0, 0.025);
          transition:
            border-color 0.25s ease,
            box-shadow 0.25s ease,
            transform 0.25s ease;
        }

        .contact-input:focus {
          outline: none;
          border-color: #35a93e;
          box-shadow:
            0 0 0 3px rgba(53, 169, 62, 0.10),
            0 4px 10px rgba(53, 169, 62, 0.06);
          transform: translateY(-1px);
        }

        .contact-submit {
          box-shadow:
            0 5px 12px rgba(53, 169, 62, 0.20);
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease,
            background-color 0.25s ease;
        }

        .contact-submit:hover:not(:disabled) {
          background-color: #2e8c36;
          transform: translateY(-2px);
          box-shadow:
            0 8px 17px rgba(53, 169, 62, 0.25);
        }

        .contact-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow:
            0 3px 7px rgba(53, 169, 62, 0.18);
        }

        @keyframes headingReveal {
          from {
            opacity: 0;
            transform: translateY(15px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 640px) {
          .contact-card:hover {
            transform: translateY(-2px);
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;

